import { getAgoDate, getTimestamp } from '@helpers/date'
import { getLocalStorage, setLocalStorage } from '@helpers/storage'
import useQuery from '@hooks/useQuery'
import { ORIGINS } from '@pages/Leads/routes/Leads/constants'
import { Lead } from '@services/leads/types'
import {
  GetAnyLeadResponse,
  useGetAnyLead
} from '@services/leads/useGetAnyLead'
import {
  FetchParams,
  GetLeadsResponse,
  useGetLeads
} from '@services/leads/useGetLeads'
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { KeyedMutator } from 'swr'

import {
  getInitialColumnsState,
  LeadColumn,
  saveColumnsAtLocalStorage
} from '../components/Table/columns'
import { ReadFilterType } from '../types'

export type LeadsProviderProps = {
  children: ReactNode
}

type FilterDates = {
  minDate: number | null
  maxDate: number | null
}

export type LeadsProviderValue = {
  isLeadsListLoading: boolean
  isAnyLeadLoading: boolean
  isListEmpty: boolean
  lead?: GetAnyLeadResponse
  leadsList?: GetLeadsResponse
  fetchParams: FetchParams
  onPageChange: (page: number) => void
  onPageSizeChange: (page: number) => void
  refreshLeadsList: () => void
  refreshCurrentLeadsList: (
    ...args: Parameters<KeyedMutator<GetLeadsResponse>>
  ) => void
  handleSearch: (value?: string) => void
  columns: LeadColumn[]
  setColumns: (columns: LeadColumn[]) => void
  spamFilter: boolean
  setSpamFilter: (value: boolean) => void
  mainLeadsAmount: number
  spamLeadsAmount: number
  onDateFilterChange: ({ minDate, maxDate }: FilterDates) => void
  setSourceFilter: (value: {
    source_uuid?: string
    source_type?: string
    group_uuid?: string
  }) => void
  setReadFilter: (value: ReadFilterType) => void
  selectedLeads: string[]
  setSelectedLeads: Dispatch<SetStateAction<string[]>>
  getCurrentLead: (closeModal: () => void) => Lead | null
  setCurrentLead: (currentLead: Lead) => void
  nextLead: () => void
  prevLead: () => void
  isCurrentLeadFirst: boolean
  isCurrentLeadLast: boolean
  hasError: boolean
}

const LeadsContext = createContext<LeadsProviderValue | null>(null)

const emptyFetchParams = { page: 1, pageSize: 0 }

const PAGE_SIZE_LOCAL_STORAGE_KEY = 'LEADS_PAGE_SIZE'

const initialFetchParams = {
  page: 1,
  pageSize: Number(getLocalStorage(PAGE_SIZE_LOCAL_STORAGE_KEY)) || 10,
  search: '',
  created_at_min: getTimestamp(getAgoDate(30)),
  created_at_max: null,
  is_read: undefined
}

export const LeadsProvider: FC<LeadsProviderProps> = ({ children }) => {
  const query = useQuery()

  const params = {
    source_uuid: query.get('source_uuid') || undefined,
    source_type: query.get('source_type') || undefined
  }

  const [fetchParams, setFetchParams] = useState<FetchParams>({
    ...initialFetchParams,
    source_type: params.source_type,
    source_uuid: params.source_uuid
  })

  const [spamFilter, setSpamFilter] = useState<boolean>(false)

  const leadsFetchParams = !spamFilter ? fetchParams : emptyFetchParams
  const spamFetchParams = spamFilter
    ? { page: fetchParams.page, pageSize: fetchParams.pageSize }
    : emptyFetchParams

  const [columns, setColumns] = useState(getInitialColumnsState())

  useEffect(() => {
    saveColumnsAtLocalStorage(columns)
  }, [columns])

  const [selectedLeads, setSelectedLeads] = useState<string[]>([])

  const {
    isLoading: isAnyMainLeadLoading,
    data: anyMainLead,
    mutate: refreshAnyMainLeadsList,
    error: anyMainLeadError
  } = useGetAnyLead({ is_spam: 0 })

  const {
    isLoading: isAnySpamLeadLoading,
    data: anySpamLead,
    mutate: refreshAnySpamLeadsList,
    error: anyLeadError
  } = useGetAnyLead({ is_spam: 1 })

  const isAnyLeadLoading = isAnyMainLeadLoading || isAnySpamLeadLoading

  const refreshAnyLeadList = useCallback(() => {
    refreshAnyMainLeadsList()
    refreshAnySpamLeadsList()
  }, [refreshAnyMainLeadsList, refreshAnySpamLeadsList])

  const {
    isLoading: isMainLeadsListLoading,
    data: mainLeadsList,
    mutate: refreshMainLeadsList,
    error: mainLeadError
  } = useGetLeads({ ...leadsFetchParams, is_spam: 0 })

  const {
    isLoading: isSpamLeadsListLoading,
    data: spamLeadsList,
    mutate: refreshSpamLeadsList,
    error: spamLeadError
  } = useGetLeads({ ...spamFetchParams, is_spam: 1 })

  useEffect(() => {
    setSelectedLeads([])
  }, [fetchParams, spamFilter])

  const mainLeadsAmount = anyMainLead?.pagination?.counter.total || 0
  const spamLeadsAmount = anySpamLead?.pagination?.counter.total || 0
  const currentLeadsListAmount = spamFilter ? spamLeadsAmount : mainLeadsAmount

  const isLeadsListLoading = !spamFilter
    ? isMainLeadsListLoading
    : isSpamLeadsListLoading

  const leadsList = !spamFilter ? mainLeadsList : spamLeadsList
  const [currentLeadIndex, setCurrentLeadIndex] = useState<number>(0)

  const getCurrentLead = useCallback(
    (closeModal: () => void) => {
      const currentLead = leadsList?.collection?.[currentLeadIndex]

      if (currentLead === undefined) {
        if (!isLeadsListLoading) {
          closeModal()
        }

        return null
      }

      return currentLead
    },
    [currentLeadIndex, leadsList, isLeadsListLoading]
  )

  const setCurrentLead = useCallback(
    (currentLead: Lead) =>
      setCurrentLeadIndex(
        leadsList?.collection.findIndex(lead => lead.id === currentLead.id)
      ),
    [leadsList]
  )

  const refreshLeadsList = useCallback(() => {
    refreshAnyLeadList()
    refreshMainLeadsList()
    refreshSpamLeadsList()
  }, [refreshMainLeadsList, refreshSpamLeadsList, refreshAnyLeadList])

  const refreshCurrentLeadsList = useCallback(
    (...args: Parameters<KeyedMutator<GetLeadsResponse>>) => {
      if (spamFilter) {
        refreshSpamLeadsList(...args)
      } else {
        refreshMainLeadsList(...args)
      }
    },
    [refreshMainLeadsList, refreshSpamLeadsList, spamFilter]
  )

  useEffect(() => {
    if (leadsList?.collection?.length === 0) {
      if (fetchParams.page > 1) {
        setFetchParams(prev => ({ ...prev, page: prev.page - 1 }))
      } else {
        refreshAnyLeadList()
      }
    }
  }, [leadsList?.collection?.length, fetchParams.page, refreshAnyLeadList])

  const handleSearch = useCallback((value?: string) => {
    setFetchParams({
      ...initialFetchParams,
      search: value || ''
    })
    setSpamFilter(false)
  }, [])

  const onPageChange = useCallback(
    (page: number) => setFetchParams(prev => ({ ...prev, page })),
    []
  )

  const onLPFilterChange = (landing_page_uuid: string) =>
    setFetchParams(prev => ({ ...prev, landing_page_uuid }))

  const onPageSizeChange = (pageSize: number) => {
    setFetchParams(prev => ({ ...prev, pageSize }))
    setLocalStorage(PAGE_SIZE_LOCAL_STORAGE_KEY, pageSize)
  }

  const onDateFilterChange = ({ minDate, maxDate }: FilterDates) =>
    setFetchParams(prev => ({
      ...prev,
      created_at_min: minDate,
      created_at_max: maxDate
    }))

  const setSourceFilter = ({
    source_uuid,
    source_type,
    group_uuid
  }: {
    source_uuid?: string
    source_type?: string
    group_uuid?: string
  }) =>
    setFetchParams(prev => ({
      ...prev,
      source_uuid,
      source_type: source_type === ORIGINS.ALL_ORIGINS ? '' : source_type,
      group_uuid
    }))

  const setReadFilter = (is_read: ReadFilterType) =>
    setFetchParams(prev => ({ ...prev, is_read }))

  const prevLead = useCallback(() => {
    if (currentLeadIndex - 1 === -1) {
      onPageChange(fetchParams.page - 1)
      setCurrentLeadIndex(fetchParams.pageSize - 1)
      return
    }

    setCurrentLeadIndex(currentLeadIndex => currentLeadIndex - 1)
  }, [currentLeadIndex, fetchParams.page, fetchParams.pageSize, onPageChange])

  const nextLead = useCallback(() => {
    if (currentLeadIndex + 1 === fetchParams.pageSize) {
      onPageChange(fetchParams.page + 1)
      setCurrentLeadIndex(0)
      return
    }

    setCurrentLeadIndex(currentLeadIndex => currentLeadIndex + 1)
  }, [currentLeadIndex, fetchParams.pageSize, fetchParams.page, onPageChange])

  const ctxValue = useMemo(
    () => ({
      isLeadsListLoading,
      leadsList,
      isAnyLeadLoading,
      isListEmpty:
        anySpamLead?.pagination.counter.total === 0 &&
        anyMainLead?.pagination.counter.total === 0,
      isCurrentLeadFirst: currentLeadIndex === 0 && fetchParams.page === 1,
      isCurrentLeadLast:
        currentLeadIndex === (leadsList?.collection?.length || 0) - 1 &&
        fetchParams.page * fetchParams.pageSize >= currentLeadsListAmount,
      fetchParams,
      onPageChange,
      onPageSizeChange,
      refreshLeadsList,
      refreshCurrentLeadsList,
      handleSearch,
      onLPFilterChange,
      columns,
      setColumns,
      spamFilter,
      setSpamFilter,
      mainLeadsAmount,
      spamLeadsAmount,
      onDateFilterChange,
      setReadFilter,
      setSourceFilter,
      selectedLeads,
      setSelectedLeads,
      getCurrentLead,
      setCurrentLead,
      nextLead,
      prevLead,
      hasError:
        !!mainLeadError ||
        !!spamLeadError ||
        !!anyMainLeadError ||
        !!anyLeadError
    }),
    [
      currentLeadsListAmount,
      currentLeadIndex,
      onPageChange,
      prevLead,
      nextLead,
      setCurrentLead,
      spamFilter,
      anyMainLead?.pagination.counter.total,
      anySpamLead?.pagination.counter.total,
      isLeadsListLoading,
      leadsList,
      isAnyLeadLoading,
      fetchParams,
      refreshLeadsList,
      refreshCurrentLeadsList,
      handleSearch,
      columns,
      mainLeadsAmount,
      spamLeadsAmount,
      selectedLeads,
      getCurrentLead,
      mainLeadError,
      spamLeadError,
      anyMainLeadError,
      anyLeadError
    ]
  )

  return (
    <LeadsContext.Provider value={ctxValue}>{children}</LeadsContext.Provider>
  )
}

export const useLeadsContext = () => {
  const ctxValue = useContext(LeadsContext)

  if (!ctxValue) {
    throw new Error('useLeadsContext must be used inside LeadsProvider')
  }

  return ctxValue
}
